import { cn } from "../../../../lib/utils";
import { whatsappPageShell, waShell } from "../../utils/whatsappPageShell";

/**
 * WhatsApp page-entry skeletons (beautiful-ui).
 * Use for initial page load only — keep button/mutation Loader2 spinners as-is.
 *
 * @see `.cursor/rules/prodyt-ui-theming.mdc`
 */

/** Pulse bone bar. */
export function WaBone({ className }) {
  return (
    <div
      className={cn(
        "animate-pulse rounded bg-gray-200 dark:bg-slate-700",
        className
      )}
      aria-hidden
    />
  );
}

/** Card shell with optional children bones. */
export function WaCardBone({ className, children }) {
  return (
    <div
      className={cn(
        "rounded-xl border border-gray-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-4 sm:p-5",
        className
      )}
    >
      {children}
    </div>
  );
}

/** Table header + N pulse rows (Contacts / Campaigns / Templates). */
export function WaTableSkeleton({ rows = 6, cols = 5, className }) {
  return (
    <div
      className={cn(
        "overflow-hidden rounded-xl border border-gray-200 dark:border-slate-800 bg-white dark:bg-slate-900",
        className
      )}
    >
      <div className="flex gap-3 border-b border-gray-100 dark:border-slate-800 px-4 py-3">
        {Array.from({ length: cols }).map((_, i) => (
          <WaBone key={`th-${i}`} className="h-3 flex-1 max-w-[100px]" />
        ))}
      </div>
      <div className="divide-y divide-gray-100 dark:divide-slate-800">
        {Array.from({ length: rows }).map((_, row) => (
          <div key={`tr-${row}`} className="flex gap-3 px-4 py-3.5">
            {Array.from({ length: cols }).map((_, col) => (
              <WaBone
                key={`td-${row}-${col}`}
                className={cn("h-4 flex-1", col === 0 && "max-w-[140px]", col === cols - 1 && "max-w-[64px]")}
              />
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

function IntroBone({ withAction }) {
  return (
    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
      <WaBone className="h-5 w-full max-w-md border-l-2 border-[hsl(158,48%,36%)]/40 pl-3" />
      {withAction ? <WaBone className="h-9 w-28 rounded-lg shrink-0" /> : null}
    </div>
  );
}

/** WhatsApp Dashboard — stats + quick actions. */
export function DashboardSkeleton() {
  return (
    <div className={waShell(whatsappPageShell.fluid)} aria-busy="true" aria-label="Loading dashboard">
      <div className="flex items-center justify-between gap-3">
        <WaBone className="h-5 w-64 max-w-full" />
        <WaBone className="h-7 w-16 rounded-full" />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <WaCardBone key={i} className="space-y-3">
            <WaBone className="h-4 w-24" />
            <WaBone className="h-8 w-20" />
            <WaBone className="h-3 w-32" />
          </WaCardBone>
        ))}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {Array.from({ length: 3 }).map((_, i) => (
          <WaCardBone key={i} className="flex items-center gap-4">
            <WaBone className="h-12 w-12 rounded-xl shrink-0" />
            <div className="flex-1 space-y-2">
              <WaBone className="h-4 w-28" />
              <WaBone className="h-3 w-40" />
            </div>
          </WaCardBone>
        ))}
      </div>
    </div>
  );
}

/** List / table pages — Campaigns, Templates, Manage Numbers, Contacts table area. */
export function ListTableSkeleton({ className, showIntro = true, rows = 6 }) {
  return (
    <div
      className={cn(waShell(whatsappPageShell.fluid), className)}
      aria-busy="true"
      aria-label="Loading list"
    >
      {showIntro ? <IntroBone withAction /> : null}
      <div className="flex flex-wrap gap-2">
        <WaBone className="h-10 flex-1 min-w-[180px] max-w-sm rounded-lg" />
        <WaBone className="h-10 w-24 rounded-lg" />
        <WaBone className="h-10 w-24 rounded-lg" />
      </div>
      <WaTableSkeleton rows={rows} cols={5} />
    </div>
  );
}

/** WorkFlows list — metric strip + rows. */
export function WorkflowListSkeleton() {
  return (
    <div className={waShell(whatsappPageShell.fluid)} aria-busy="true" aria-label="Loading workflows">
      <IntroBone withAction />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <WaCardBone key={i} className="space-y-2">
            <WaBone className="h-3 w-16" />
            <WaBone className="h-7 w-12" />
          </WaCardBone>
        ))}
      </div>
      <div className="space-y-3">
        {Array.from({ length: 4 }).map((_, i) => (
          <WaCardBone key={i} className="flex items-center gap-4">
            <WaBone className="h-10 w-10 rounded-lg shrink-0" />
            <div className="flex-1 space-y-2 min-w-0">
              <WaBone className="h-4 w-48 max-w-full" />
              <WaBone className="h-3 w-32 max-w-full" />
            </div>
            <WaBone className="h-8 w-20 rounded-lg shrink-0 hidden sm:block" />
          </WaCardBone>
        ))}
      </div>
    </div>
  );
}

/** Send Message / Create Template — form + preview. */
export function HybridFormPreviewSkeleton() {
  return (
    <div className={waShell(whatsappPageShell.hybrid)} aria-busy="true" aria-label="Loading form">
      <IntroBone />
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 lg:gap-8">
        <div className="lg:col-span-3 space-y-4">
          {Array.from({ length: 3 }).map((_, i) => (
            <WaCardBone key={i} className="space-y-4">
              <div className="flex items-center gap-3">
                <WaBone className="h-8 w-8 rounded-full shrink-0" />
                <WaBone className="h-4 w-36" />
              </div>
              <WaBone className="h-10 w-full rounded-lg" />
              <WaBone className="h-10 w-full rounded-lg" />
            </WaCardBone>
          ))}
        </div>
        <div className="lg:col-span-2">
          <WaCardBone className="space-y-4 flex flex-col items-center py-8">
            <WaBone className="h-4 w-40" />
            <WaBone className="h-[420px] w-[240px] max-w-full rounded-[2rem]" />
          </WaCardBone>
        </div>
      </div>
    </div>
  );
}

/** Chat fill — conversation list + main pane. */
export function ChatSkeleton() {
  return (
    <div
      className={waShell(
        whatsappPageShell.fill,
        "flex-1 min-h-0 -m-3 overflow-hidden flex border border-gray-200 dark:border-slate-800 bg-white dark:bg-slate-900"
      )}
      aria-busy="true"
      aria-label="Loading chat"
    >
      <div className="w-full md:w-[350px] xl:w-[380px] shrink-0 flex flex-col border-r border-gray-200 dark:border-slate-800 bg-slate-50/80 dark:bg-slate-950">
        <div className="p-3 space-y-3 border-b border-gray-200 dark:border-slate-800 bg-white dark:bg-slate-900">
          <div className="flex gap-2">
            <WaBone className="h-10 flex-1 rounded-xl" />
            <WaBone className="h-10 w-10 rounded-xl shrink-0" />
          </div>
          <div className="flex gap-1 p-1 rounded-xl bg-slate-100 dark:bg-slate-800/80">
            <WaBone className="h-9 flex-1 rounded-lg" />
            <WaBone className="h-9 flex-1 rounded-lg" />
          </div>
        </div>
        <div className="flex-1 overflow-hidden bg-white dark:bg-slate-900 divide-y divide-gray-100 dark:divide-slate-800">
          {Array.from({ length: 7 }).map((_, i) => (
            <div key={i} className="flex gap-3 px-3 py-3">
              <WaBone className="h-12 w-12 rounded-full shrink-0" />
              <div className="flex-1 space-y-2 min-w-0 pt-1">
                <WaBone className="h-3.5 w-28" />
                <WaBone className="h-3 w-full max-w-[180px]" />
              </div>
            </div>
          ))}
        </div>
      </div>
      <div className="hidden md:flex flex-1 flex-col min-w-0 bg-[#efeae2] dark:bg-slate-950">
        <div className="h-14 border-b border-gray-200 dark:border-slate-800 bg-white dark:bg-slate-900 px-4 flex items-center gap-3">
          <WaBone className="h-10 w-10 rounded-full shrink-0" />
          <div className="space-y-2 flex-1">
            <WaBone className="h-3.5 w-32" />
            <WaBone className="h-3 w-20" />
          </div>
        </div>
        <div className="flex-1 p-6 space-y-4">
          <WaBone className="h-12 w-2/3 max-w-sm rounded-2xl rounded-tl-sm" />
          <WaBone className="h-16 w-1/2 max-w-xs ml-auto rounded-2xl rounded-tr-sm" />
          <WaBone className="h-10 w-1/3 max-w-[200px] rounded-2xl rounded-tl-sm" />
        </div>
        <div className="p-3 border-t border-gray-200 dark:border-slate-800 bg-white dark:bg-slate-900">
          <WaBone className="h-12 w-full rounded-xl" />
        </div>
      </div>
    </div>
  );
}

/** Analytics pages — KPI cards + chart + table. */
export function AnalyticsSkeleton({ embedded = false }) {
  const body = (
    <>
      {!embedded ? <IntroBone withAction /> : null}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <WaCardBone key={i} className="space-y-2">
            <WaBone className="h-3 w-20" />
            <WaBone className="h-7 w-16" />
          </WaCardBone>
        ))}
      </div>
      <WaCardBone className="space-y-4">
        <WaBone className="h-4 w-40" />
        <WaBone className="h-56 w-full rounded-lg" />
      </WaCardBone>
      <WaTableSkeleton rows={5} cols={4} />
    </>
  );

  if (embedded) {
    return (
      <div className="space-y-5" aria-busy="true" aria-label="Loading analytics">
        {body}
      </div>
    );
  }

  return (
    <div className={waShell(whatsappPageShell.fluid, "!space-y-4 sm:!space-y-5")} aria-busy="true" aria-label="Loading analytics">
      {body}
    </div>
  );
}

/** Billing — tabs + plan cards. */
export function BillingSkeleton({ embedded = false }) {
  const body = (
    <>
      {!embedded ? <IntroBone /> : null}
      <div className="flex gap-6 border-b border-gray-200 dark:border-slate-800 pb-0">
        <WaBone className="h-8 w-36 rounded-none" />
        <WaBone className="h-8 w-28 rounded-none" />
        <WaBone className="h-8 w-24 rounded-none" />
      </div>
      <div className="flex justify-end">
        <WaBone className="h-9 w-40 rounded-lg" />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 max-w-5xl">
        {Array.from({ length: 3 }).map((_, i) => (
          <WaCardBone key={i} className="space-y-4 min-h-[220px]">
            <WaBone className="h-5 w-24" />
            <WaBone className="h-8 w-32" />
            <WaBone className="h-3 w-full" />
            <WaBone className="h-3 w-4/5" />
            <WaBone className="h-3 w-3/5" />
            <WaBone className="h-10 w-full rounded-lg mt-auto" />
          </WaCardBone>
        ))}
      </div>
    </>
  );

  if (embedded) {
    return (
      <div className="space-y-6" aria-busy="true" aria-label="Loading billing">
        {body}
      </div>
    );
  }

  return (
    <div
      className={waShell("w-full min-h-full", "bg-slate-50 dark:bg-slate-950", "px-4 sm:px-6 lg:px-8 py-5 sm:py-6", "space-y-6")}
      aria-busy="true"
      aria-label="Loading billing"
    >
      {body}
    </div>
  );
}

/** Workflow builder fill. */
export function BuilderSkeleton() {
  return (
    <div
      className="flex flex-col flex-1 min-h-0 min-w-0 overflow-hidden -m-3 bg-white dark:bg-slate-950"
      aria-busy="true"
      aria-label="Loading workflow builder"
    >
      <div className="h-14 shrink-0 border-b border-gray-200 dark:border-slate-800 flex items-center gap-3 px-4">
        <WaBone className="h-8 w-8 rounded-lg" />
        <WaBone className="h-4 w-40" />
        <div className="flex-1" />
        <WaBone className="h-9 w-20 rounded-lg" />
        <WaBone className="h-9 w-24 rounded-lg" />
      </div>
      <div className="flex flex-1 min-h-0">
        <div className="w-64 shrink-0 border-r border-gray-200 dark:border-slate-800 p-3 space-y-3 hidden md:block">
          <WaBone className="h-8 w-full rounded-lg" />
          {Array.from({ length: 8 }).map((_, i) => (
            <WaBone key={i} className="h-9 w-full rounded-lg" />
          ))}
        </div>
        <div className="flex-1 min-w-0 relative bg-slate-50 dark:bg-slate-950 p-6">
          <div
            className="absolute inset-0 opacity-40"
            style={{
              backgroundImage:
                "radial-gradient(circle at 1px 1px, rgb(148 163 184 / 0.4) 1px, transparent 0)",
              backgroundSize: "20px 20px",
            }}
            aria-hidden
          />
          <div className="relative space-y-4 max-w-sm">
            <WaBone className="h-16 w-48 rounded-xl" />
            <WaBone className="h-20 w-56 rounded-xl ml-8" />
            <WaBone className="h-16 w-44 rounded-xl ml-4" />
          </div>
        </div>
      </div>
    </div>
  );
}

/** Settings / basic automations focused form. */
export function FocusedFormSkeleton() {
  return (
    <div className={waShell(whatsappPageShell.focused)} aria-busy="true" aria-label="Loading settings">
      <IntroBone />
      <WaCardBone className="space-y-5">
        <WaBone className="h-5 w-48" />
        <WaBone className="h-10 w-full rounded-lg" />
        <WaBone className="h-10 w-full rounded-lg" />
        <WaBone className="h-24 w-full rounded-lg" />
        <div className="flex justify-end gap-2 pt-2">
          <WaBone className="h-10 w-24 rounded-lg" />
          <WaBone className="h-10 w-28 rounded-lg" />
        </div>
      </WaCardBone>
    </div>
  );
}

/**
 * Pre-MenuLayout session check — soft app shell silhouette.
 */
export function AuthGateSkeleton() {
  return (
    <div
      className="fixed inset-0 flex w-screen h-[100dvh] overflow-hidden bg-slate-50 dark:bg-slate-950"
      aria-busy="true"
      aria-label="Checking session"
    >
      <div className="hidden sm:flex w-56 shrink-0 flex-col gap-3 p-4 bg-[hsl(217,33%,10%)]">
        <WaBone className="h-8 w-32 bg-slate-600/80 mb-4" />
        {Array.from({ length: 8 }).map((_, i) => (
          <WaBone key={i} className="h-9 w-full rounded-lg bg-slate-700/70" />
        ))}
      </div>
      <div className="flex min-h-0 min-w-0 flex-1 flex-col">
        <div className="h-[50px] sm:h-[60px] shrink-0 border-b border-gray-200 dark:border-slate-800 bg-white dark:bg-slate-900 px-4 flex items-center justify-between">
          <WaBone className="h-5 w-28" />
          <div className="flex gap-2">
            <WaBone className="h-8 w-24 rounded-lg" />
            <WaBone className="h-8 w-28 rounded-lg hidden sm:block" />
            <WaBone className="h-8 w-8 rounded-lg" />
          </div>
        </div>
        <div className="flex-1 p-4 sm:p-6 space-y-4 overflow-hidden">
          <WaBone className="h-5 w-72 max-w-full" />
          <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
            {Array.from({ length: 4 }).map((_, i) => (
              <WaCardBone key={i} className="space-y-3">
                <WaBone className="h-4 w-20" />
                <WaBone className="h-7 w-16" />
              </WaCardBone>
            ))}
          </div>
          <WaTableSkeleton rows={4} cols={4} />
        </div>
      </div>
    </div>
  );
}

/**
 * Capabilities / account-setup gate inside MenuLayout main pane.
 */
export function PostLoginGateSkeleton() {
  return (
    <div
      className="flex min-h-full w-full flex-1 flex-col gap-6 p-1"
      aria-busy="true"
      aria-label="Loading account"
    >
      <WaBone className="h-5 w-64 max-w-full" />
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <WaCardBone key={i} className="space-y-3">
            <WaBone className="h-4 w-24" />
            <WaBone className="h-8 w-16" />
            <WaBone className="h-3 w-28" />
          </WaCardBone>
        ))}
      </div>
      <WaCardBone className="space-y-4 flex-1 min-h-[200px]">
        <WaBone className="h-4 w-40" />
        <WaBone className="h-40 w-full rounded-lg" />
        <div className="grid grid-cols-3 gap-3">
          <WaBone className="h-16 rounded-lg" />
          <WaBone className="h-16 rounded-lg" />
          <WaBone className="h-16 rounded-lg" />
        </div>
      </WaCardBone>
    </div>
  );
}
